import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { gsap } from "gsap";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowRight,
  UserPlus,
  CheckCircle,
  AlertCircle,
  Loader2,
  Zap,
  DollarSign,
  Shield,
  TrendingUp,
} from "lucide-react";
import GooglePopup from "./ui/GooglePopupLogin";


const Register = () => {
  const { register, loading: globalLoading } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [gridDimensions, setGridDimensions] = useState({ rows: 0, cols: 0 });
  const [referralCode, setReferralCode] = useState(null);
  const [referrerName, setReferrerName] = useState(null);
  const [userType, setUserType] = useState("affiliate");
  const navigate = useNavigate();

  // Refs for animations
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const formRef = useRef(null);
  const strengthMeterRef = useRef(null);

  useEffect(() => {
    // Set grid dimensions after component mounts
    const updateGridDimensions = () => {
      setGridDimensions({
        rows: Math.ceil(window.innerHeight / 40),
        cols: Math.ceil(window.innerWidth / 40),
      });
    };

    updateGridDimensions();
    window.addEventListener("resize", updateGridDimensions);

    return () => window.removeEventListener("resize", updateGridDimensions);
  }, []);

  useEffect(() => {
    // Check for referral code from URL parameters or localStorage
    const urlParams = new URLSearchParams(location.search);
    const refFromUrl = urlParams.get("ref");
    const refFromStorage = localStorage.getItem("referralCode");

    const referralCodeFound = refFromUrl || refFromStorage;

    console.log("🔍 Referral Detection Debug:");
    console.log("  URL params:", location.search);
    console.log("  refFromUrl:", refFromUrl);
    console.log("  refFromStorage:", refFromStorage);
    console.log("  referralCodeFound:", referralCodeFound);

    if (referralCodeFound) {
      setReferralCode(referralCodeFound);
      setReferrerName(referralCodeFound);

      // Store in localStorage if it came from URL
      if (refFromUrl) {
        localStorage.setItem("referralCode", refFromUrl);
        console.log("  ✅ Stored referral code in localStorage:", refFromUrl);
      }

      console.log("  ✅ Referral code detected and set:", referralCodeFound);
    } else {
      console.log("  ❌ No referral code found");
    }
  }, [location.search]);

  useEffect(() => {
    // Initial animations
    const tl = gsap.timeline();

    // Set initial states - opposite direction from login
    gsap.set([leftPanelRef.current, rightPanelRef.current], {
      opacity: 0,
      x: 50,
    });

    gsap.set(leftPanelRef.current, {
      x: -50,
    });

    // Animate in
    tl.to([leftPanelRef.current, rightPanelRef.current], {
      opacity: 1,
      x: 0,
      duration: 0.8,
      ease: "power3.out",
      stagger: 0.2,
    });
  }, []);

  useEffect(() => {
    // Calculate password strength
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 25;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 25;

    setPasswordStrength(strength);

    // Animate strength meter
    if (strengthMeterRef.current && password.length > 0) {
      gsap.to(strengthMeterRef.current, {
        width: `${strength}%`,
        duration: 0.3,
        ease: "power2.out",
      });
    }
  }, [password]);

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return "bg-red-500";
    if (passwordStrength <= 50) return "bg-yellow-500";
    if (passwordStrength <= 75) return "bg-blue-500";
    return "bg-green-500";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 25) return "Weak";
    if (passwordStrength <= 50) return "Fair";
    if (passwordStrength <= 75) return "Good";
    return "Strong";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    console.log("🚀 Registration Debug:");
    console.log("  Username:", username);
    console.log("  Email:", email);
    console.log("  Referral Code:", referralCode);
    console.log("  User Type:", userType);
    console.log("  Calling register with referralCode and userType...");

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const result = await register(
        username,
        email,
        password,
        referralCode,
        userType
      );

      console.log("  ✅ Registration result:", result);

      if (result.success) {
        // Clear referral code from localStorage if it was used
        if (referralCode) {
          localStorage.removeItem("referralCode");

          // Add referral success message
          setSuccess(
            `Account created successfully! 🎉 You've been connected to ${referralCode} as your referrer. Please check your email and click the verification link to activate your account.`
          );
        } else {
          setSuccess(result.message);

          if (result.requiresVerification) {
            // Show success message and instructions
            setSuccess(
              "Account created successfully! 🎉 Please check your email and click the verification link to activate your account."
            );
          } else {
            // Redirect to dashboard if no verification needed
            setTimeout(() => {
              navigate("/dashboard");
            }, 2000);
          }
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      if (error.message?.includes("already registered")) {
        setError(
          "An account with this email already exists. Please sign in instead."
        );
      } else if (error.message?.includes("Password")) {
        setError("Password must be at least 6 characters long.");
      } else {
        setError(error.message || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden"
    >
      {/* Grid background with center glow effect */}
      <div className="absolute inset-0">
        {/* Base grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Radial gradient mask to create center glow and edge fade effect */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                rgba(184, 70, 14, 0.15) 0%, 
                rgba(184, 70, 14, 0.08) 40%, 
                rgba(184, 70, 14, 0.03) 70%,
                rgba(184, 70, 14, 0) 100%
              )
            `,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
            maskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
            WebkitMaskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
          }}
        />

        {/* Interactive grid overlay for hover effects */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: gridDimensions.rows }).map((_, row) =>
            Array.from({ length: gridDimensions.cols }).map((_, col) => (
              <div
                key={`${row}-${col}`}
                className="absolute w-10 h-10 pointer-events-auto transition-all duration-300"
                style={{
                  left: `${col * 40}px`,
                  top: `${row * 40}px`,
                  animation: "none",
                }}
                onMouseEnter={(e) => {
                  gsap.fromTo(
                    e.target,
                    {
                      backgroundColor: "rgba(184, 70, 14, 0)",
                      boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      scale: 1,
                    },
                    {
                      backgroundColor: "rgba(184, 70, 14, 0.3)",
                      boxShadow:
                        "0 0 25px rgba(184, 70, 14, 0.8), inset 0 0 15px rgba(184, 70, 14, 0.3)",
                      scale: 1.05,
                      duration: 0.2,
                      ease: "power2.out",
                    }
                  );

                  const adjacentSquares = document.querySelectorAll(
                    `[data-row="${row}"][data-col="${
                      col - 1
                    }"], [data-row="${row}"][data-col="${
                      col + 1
                    }"], [data-row="${
                      row - 1
                    }"][data-col="${col}"], [data-row="${
                      row + 1
                    }"][data-col="${col}"]`
                  );
                  adjacentSquares.forEach((square, index) => {
                    gsap.fromTo(
                      square,
                      {
                        backgroundColor: "rgba(184, 70, 14, 0)",
                        boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      },
                      {
                        backgroundColor: "rgba(184, 70, 14, 0.1)",
                        boxShadow: "0 0 10px rgba(184, 70, 14, 0.4)",
                        duration: 0.15,
                        delay: index * 0.05,
                        ease: "power2.out",
                        onComplete: () => {
                          gsap.to(square, {
                            backgroundColor: "rgba(184, 70, 14, 0)",
                            boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                            duration: 0.3,
                            ease: "power2.out",
                          });
                        },
                      }
                    );
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    backgroundColor: "rgba(184, 70, 14, 0)",
                    boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                    scale: 1,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                }}
                data-row={row}
                data-col={col}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Centered Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left Panel - Marketing Content */}
            <div
              ref={leftPanelRef}
              className="w-full lg:w-1/2 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] p-4 sm:p-6 lg:p-8 flex items-center justify-center relative overflow-hidden order-1"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-white text-center max-w-md">
                <div className="mb-4 lg:mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 lg:w-16 lg:h-16 bg-white/20 rounded-3xl mb-3 lg:mb-4 backdrop-blur-sm">
                    <Zap className="w-7 h-7 lg:w-8 lg:h-8" />
                  </div>
                  <h2 className="text-xl lg:text-3xl font-bold mb-3 lg:mb-4 leading-tight">
                    Start Your
                    <span className="block bg-gradient-to-r from-orange-300 to-yellow-300 bg-clip-text text-transparent">
                      Success Story
                    </span>
                  </h2>
                  <p className="text-sm lg:text-lg text-orange-100 leading-relaxed mb-4 lg:mb-6">
                    Join our platform and unlock unlimited earning potential
                    with cutting-edge tools and AI-powered insights.
                  </p>
                </div>

                {/* Benefits */}
                <div className="space-y-2 lg:space-y-3 mb-4 lg:mb-6">
                  <div className="flex items-center text-left bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <DollarSign className="w-4 h-4 lg:w-5 lg:h-5 mr-3 text-orange-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-xs lg:text-sm">
                        Instant Payouts
                      </h4>
                      <p className="text-xs text-orange-100">
                        Get paid within 24 hours
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center text-left bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <Shield className="w-4 h-4 lg:w-5 lg:h-5 mr-3 text-orange-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-xs lg:text-sm">
                        100% Secure
                      </h4>
                      <p className="text-xs text-orange-100">
                        Bank-grade security & encryption
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center text-left bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <Zap className="w-4 h-4 lg:w-5 lg:h-5 mr-3 text-orange-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-xs lg:text-sm">
                        Zero Setup Fees
                      </h4>
                      <p className="text-xs text-orange-100">
                        Start earning immediately
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 lg:gap-3 mb-4 lg:mb-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2.5 lg:p-3 text-center">
                    <div className="text-lg lg:text-xl font-bold text-orange-300">
                      $435k
                    </div>
                    <div className="text-xs text-orange-200">
                      Paid to Affiliates
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2.5 lg:p-3 text-center">
                    <div className="text-lg lg:text-xl font-bold text-orange-300">
                      98%
                    </div>
                    <div className="text-xs text-orange-200">Retention Rate</div>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 lg:p-4">
                  <p className="text-xs lg:text-sm mb-2 lg:mb-3">
                    💰 <strong>Elite Earnings:</strong> Earn $1,000+ a day 
                    like our top affiliates!
                  </p>
                  <div className="text-xs text-orange-200">
                    Join the high earners - Start your journey today!
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Register Form */}
            <div
              ref={rightPanelRef}
              className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 flex items-center justify-center order-2"
            >
              <div className="w-full max-w-sm">
                <div ref={formRef}>
                  {/* Logo */}
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] rounded-2xl flex items-center justify-center shadow-lg">
                      <UserPlus className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-6">
                    <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      Create Account
                    </h1>
                    <p className="text-sm lg:text-base text-gray-300">
                      Start your affiliate journey today
                    </p>
                  </div>

                  {/* Referral Banner */}
                  {referralCode && (
                    <div className="bg-gradient-to-r from-[#B8460E]/20 to-orange-500/20 border border-orange-400/50 rounded-xl p-4 mb-6">
                      <div className="flex items-center justify-center mb-2">
                        <div className="w-8 h-8 bg-gradient-to-r from-[#B8460E] to-orange-500 rounded-full flex items-center justify-center mr-3">
                          <UserPlus className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-orange-200">
                            🎉 Referral Bonus!
                          </h3>
                          <p className="text-sm text-orange-300">
                            Invited by{" "}
                            <span className="font-bold">{referrerName}</span>
                          </p>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-orange-200 bg-orange-900/30 rounded-lg px-3 py-1 inline-block">
                          They'll earn 10% bonus on your future earnings!
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Registration Form */}
                  <form onSubmit={handleSubmit} className="space-y-3">
                    {error && (
                      <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-2.5 rounded-xl text-sm flex items-center">
                        <AlertCircle className="w-4 h-4 lg:w-5 lg:h-5 mr-2 flex-shrink-0" />
                        {error}
                      </div>
                    )}

                    {success && (
                      <div className="bg-green-900/20 border border-green-500/50 text-green-300 px-4 py-2.5 rounded-xl text-sm flex items-center">
                        <CheckCircle className="w-4 h-4 lg:w-5 lg:h-5 mr-2 flex-shrink-0" />
                        {success}
                      </div>
                    )}

                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1.5">
                        Username
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 lg:pl-12 pr-4 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Choose a username"
                          required
                          autoFocus
                          disabled={loading || globalLoading}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1.5">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 lg:pl-12 pr-4 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Enter your email"
                          required
                          disabled={loading || globalLoading}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1.5">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 lg:pl-12 pr-10 lg:pr-12 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Create a password"
                          required
                          minLength={6}
                          disabled={loading || globalLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" />
                          ) : (
                            <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
                          )}
                        </button>
                      </div>

                      {/* Password strength meter */}
                      {password.length > 0 && (
                        <div className="mt-1.5">
                          <div className="flex justify-between text-xs text-gray-300 mb-1">
                            <span>Password Strength</span>
                            <span className="font-semibold">
                              {getPasswordStrengthText()}
                            </span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-1">
                            <div
                              ref={strengthMeterRef}
                              className={`h-1 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                              style={{ width: "0%" }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1.5">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 lg:pl-12 pr-10 lg:pr-12 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Confirm your password"
                          required
                          disabled={loading || globalLoading}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" />
                          ) : (
                            <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* User Type Selection - COMMENTED OUT: All users default to affiliate */}
                    {/*<div>
                      <label className="block text-gray-300 text-sm font-semibold mb-2">
                        Account Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="userType"
                            value="affiliate"
                            checked={userType === "affiliate"}
                            onChange={(e) => setUserType(e.target.value)}
                            className="sr-only"
                            disabled={loading || globalLoading}
                          />
                          <div
                            className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                              userType === "affiliate"
                                ? "border-[#B8460E] bg-[#B8460E]/20"
                                : "border-white/20 bg-white/5 hover:border-white/30"
                            }`}
                          >
                            <div className="flex items-center justify-center mb-2">
                              <User className="w-5 h-5 text-[#B8460E]" />
                            </div>
                            <div className="text-center">
                              <div className="font-semibold text-sm text-white">
                                Affiliate
                              </div>
                              <div className="text-xs text-gray-300">
                                Performance marketing
                              </div>
                            </div>
                          </div>
                        </label>

                        <label className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="userType"
                            value="media_buyer"
                            checked={userType === "media_buyer"}
                            onChange={(e) => setUserType(e.target.value)}
                            className="sr-only"
                            disabled={loading || globalLoading}
                          />
                          <div
                            className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                              userType === "media_buyer"
                                ? "border-[#B8460E] bg-[#B8460E]/20"
                                : "border-white/20 bg-white/5 hover:border-white/30"
                            }`}
                          >
                            <div className="flex items-center justify-center mb-2">
                              <TrendingUp className="w-5 h-5 text-[#B8460E]" />
                            </div>
                           <div className="text-center">
                              <div className="font-semibold text-sm text-white">
                                Media Buyer
                              </div>
                              <div className="text-xs text-gray-300">
                                Campaign management
                              </div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>*/}

                    <button
                      type="submit"
                      disabled={loading || globalLoading}
                      className="w-full bg-gradient-to-r from-[#B8460E] via-[#8B3A0E] to-[#B8460E] hover:from-[#D4541A] hover:via-[#B8460E] hover:to-[#D4541A] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 lg:py-3 px-6 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center text-sm lg:text-base shadow-lg hover:shadow-xl"
                    >
                      {loading || globalLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 mr-2 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        <>
                          Create account
                          <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Footer Links */}
                   <div className="mt-4 pt-4 border-t border-white/20 space-y-2 text-center">
                    {/* <p className="text-sm text-white/60 mb-3">Or continue with</p> */}
                    <div className="flex justify-center items-center gap-3">
                      <GooglePopup/>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20 space-y-2 text-center">
                    <p className="text-gray-300 text-sm">
                      Already have an account?{" "}
                      <Link
                        to="/login"
                        className="text-[#B8460E] hover:text-[#D4541A] transition-colors font-semibold"
                      >
                        Sign in
                      </Link>
                    </p>
                  </div>
                   
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
